import React, { useState, useEffect, useContext } from 'react';
import { withRouter, Switch, Route, Redirect } from 'react-router-dom';
import _ from 'lodash';
import { connect } from 'react-redux';
import ConfirmAlert from 'modules/common/components/ConfirmAlert';
import GenericListPair from 'modules/common/components/GenericListPair';
import useDebounce from 'modules/common/hooks/useDebounce';
import { ModalContext } from 'App';
import PropTypes from 'prop-types';
import * as actions from '../action';
import * as c from '../constant';
import PurposesContent from './PurposesContent';
import CreateModal from '../components/CreateModal';
import UpdateModal from '../components/UpdateModal';

const INIT_FILTER = {
  sort_by: 'desc',
  q: '',
};

function PurposesContainer({
  match,
  history,
  getPurposeList,
  purposeList,
  purposeListPager,
  isLoading,
  removePurpose,
  showPurpose,
}) {
  const [filter, setFilter] = useState(INIT_FILTER);
  const debouncedFilter = useDebounce(filter, 500);
  const setModal = useContext(ModalContext);

  useEffect(() => {
    getPurposeList(debouncedFilter);

    return () => {
      setModal({ isOpen: false });
    };
  }, [JSON.stringify(debouncedFilter)]);

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    setFilter((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddModal = (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: <CreateModal />,
      title: 'Add Purpose',
      modalSize: 'modal-md',
    });
  };

  const handleEdit = (data) => (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: <UpdateModal updateList={data.attributes} id={data.id} />,
      title: 'Update Purpose',
      modalSize: 'modal-md',
    });
  };

  const handleRemove = (data) => (e) => {
    e.preventDefault();
    ConfirmAlert(
      'Are you sure, you want to remove this purpose?',
      () => {
        removePurpose(data.id, () => {
          getPurposeList(INIT_FILTER);
          const id = _.get(purposeList, '0.id');
          history.push(`/general-settings/purposes/${id}`);
          showPurpose(id);
        });
      },
      () => {},
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      }
    );
  };

  return (
    <Route
      path={`${match.path}/:purposeId?`}
      render={({ match: { params } }) => (
        <GenericListPair
          moduleName="Purposes"
          setKeyword={handleOnChange}
          keyword={_.get(filter, 'q') || ''}
          keywordPlaceholder="Search Purpose..."
          pathName={`${match.path}/:purposeId?`}
          listName="Purposes"
          label="Purpose List"
          renderItem={(item) => (
            <div className="px-3 py-3">{_.get(item, 'attributes.name')}</div>
          )}
          isLoading={isLoading}
          selected={params.purposeId}
          data={purposeList}
          dataPager={purposeListPager}
          handleSelectRow={(data) => {
            history.push(`${match.url}/${_.get(data, 'id')}`);
          }}
          renderContent={() => (
            <React.Fragment>
              <Switch>
                <Route
                  path={`${match.path}/:purposeId?`}
                  render={() => (
                    <PurposesContent
                      handleAddModal={handleAddModal}
                      handleEdit={handleEdit}
                      handleRemove={handleRemove}
                    />
                  )}
                />
              </Switch>
              <Route
                path={`${match.path}/:purposeId?`}
                render={() =>
                  !params.purposeId && purposeList.length > 0 ? (
                    <Redirect
                      to={`${match.url}/${_.get(purposeList, '0.id')}`}
                    />
                  ) : null
                }
              />
            </React.Fragment>
          )}
          handleChangePage={(page) =>
            setFilter((prev) => ({ ...prev, page }))
          }
        />
      )}
    />
  );
}

PurposesContainer.propTypes = {
  match: PropTypes.instanceOf(Object).isRequired,
  history: PropTypes.instanceOf(Object).isRequired,
  getPurposeList: PropTypes.instanceOf(Function).isRequired,
  purposeList: PropTypes.instanceOf(Array).isRequired,
  purposeListPager: PropTypes.instanceOf(Object).isRequired,
  removePurpose: PropTypes.instanceOf(Function).isRequired,
  isLoading: PropTypes.bool.isRequired,
  showPurpose: PropTypes.instanceOf(Function).isRequired,
};

const mapStateToProps = ({ api }) => ({
  purposeList: _.get(api, `${c.GET_PURPOSE}.list`) || [],
  purposeListPager: _.get(api, `${c.GET_PURPOSE}.pager`) || {},
  isLoading: _.get(api, 'loading', []).indexOf(c.GET_PURPOSE) > -1,
});

const enhance = _.flowRight([withRouter, connect(mapStateToProps, actions)]);

export default enhance(PurposesContainer);
